import { useState, MouseEvent, memo, useMemo, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Menu, MenuItem } from '@mui/material'
import { BioCalendarRow, SporeTestReportCustomer } from '../types'
import { exportAllResults, exportFailedTestResults, exportLastResults, exportResultsByPractice } from '../utils/exports'
import BiologicalExportModal from './BiologicalExportModal'
import { Dayjs } from 'dayjs'
import { addSuccess } from '../../../../redux/alerts'
import { useAppDispatch } from '../../../../redux/store'
import { getResultsByPractice } from '../services'
import { AutoclaveType, Frequency } from '../../../practice/biological/types'
import { getAutoclaveFrequencies, getAutoclaveTypes } from '../../../../services/biologicalServices'
import { selectChildPractices } from '../../../../redux/customer'
import { useSelector } from 'react-redux'

type BiologicalExportButtonProps = {
  calendarData: BioCalendarRow[] | null
  customerNumber: string
  fromDate: Dayjs
  toDate: Dayjs
  groupIds: number[]
}

const BiologicalExportButton = (props: BiologicalExportButtonProps) => {
  const { calendarData, customerNumber, fromDate, toDate, groupIds } = props

  const [exportOpenEl, setExportOpenEl] = useState<EventTarget & HTMLButtonElement | null>(null)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [autoclaveTypes, setAutoclaveTypes] = useState<AutoclaveType[]>([])
  const [autoclaveFrequencies, setAutoclaveFrequencies] = useState<Frequency[]>([])

  const dispatch = useAppDispatch()

  const childPractices = useSelector(selectChildPractices)

  const fetchAutoclaveTypes = async (customerNumber: string) => {
    if (customerNumber) {
      const types = await getAutoclaveTypes(customerNumber)
      setAutoclaveTypes(types)
    }
  }

  const fetchAutoclaveFrequencies = async (customerNumber: string) => {
    if (customerNumber) {
      const frequencies = await getAutoclaveFrequencies(customerNumber)
      setAutoclaveFrequencies(frequencies)
    }
  }  

  useEffect(() => {
    if (autoclaveTypes.length === 0) {
      fetchAutoclaveTypes(customerNumber)
    }
  }, [customerNumber, autoclaveTypes])

  useEffect(() => {
    if (autoclaveFrequencies.length === 0) {
      fetchAutoclaveFrequencies(customerNumber)
    }
  }, [customerNumber, autoclaveFrequencies])

  const handleExportClose = () => {
    setExportOpenEl(null)
  }

  const handleExportModalClose = () => {
    setExportModalOpen(false)
  }

  const handleExportClick = (event: MouseEvent<HTMLButtonElement>) => {
    setExportOpenEl(event.currentTarget)
  }

  const exportOpen = Boolean(exportOpenEl)

  const handleAllResults = async () => {
    setLoading(true)
    handleExportClose()
    await exportAllResults(calendarData)
    setLoading(false)
  }

  const handleResultsByPractice = async () => {
    setLoading(true)

    handleExportClose()

    const practices = await getResultsByPractice(customerNumber, groupIds)

    const children = practices.filter((practice: SporeTestReportCustomer) => {
      return allCustomerNumbers.has(practice.customer_number)
    })

    const message = exportResultsByPractice(children, autoclaveTypes, autoclaveFrequencies)

    console.log(message)

    if (message) {
      dispatch(addSuccess(message))
    }

    setLoading(false)
  }

  const handleLastResults = async () => {
    setLoading(true)
    handleExportClose()
    await exportLastResults(calendarData, childPractices)
    setLoading(false)
  }

  const handleFailedTestReport = async () => {
    setLoading(true)
    handleExportClose()
    setExportModalOpen(true)
    setLoading(false)
  }

  const handleExportFailedTestReport = async (fromDate?: Dayjs, toDate?: Dayjs) => {
    setLoading(true)
    handleExportModalClose()
    const message = await exportFailedTestResults(calendarData, customerNumber, groupIds, fromDate, toDate)
    if (message) {
      dispatch(addSuccess(message))
    }
    setLoading(false)
  }

  const allCustomerNumbers = useMemo(() => {
    return new Set(
      calendarData?.map(customer => customer.customerNumber) ?? []
    )
  }, [calendarData])  

  return (
    <>
      <div className="dropdown">
        <Button
          className='m-r-12'
          aria-haspopup="true"
          variant="contained"
          name="export"
          onClick={handleExportClick}
          disabled={loading}
        >
          {!loading ? 'Export' : 'Exporting...'}
          <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" color='white' />
        </Button>
        <Menu
          id="account-menu"
          anchorEl={exportOpenEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          open={exportOpen}
          onClose={handleExportClose}
          MenuListProps={{
            'aria-labelledby': 'export-button'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          <MenuItem onClick={handleAllResults}>
            All Results
          </MenuItem>
          <MenuItem onClick={handleResultsByPractice}>
            Results by Practice
          </MenuItem>
          <MenuItem onClick={handleLastResults}>
            Last Test Results
          </MenuItem>
          <MenuItem onClick={handleFailedTestReport}>
            Failed Test Report
          </MenuItem>
        </Menu>
      </div>
      <BiologicalExportModal
        open={exportModalOpen}
        handleClose={handleExportModalClose}
        handleSubmit={(fromDate, toDate) => {
          handleExportFailedTestReport(fromDate, toDate)
        }}
        fromDate={fromDate}
        toDate={toDate}
      />
    </>
  )
}

export default memo(BiologicalExportButton)
